import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Trash2, Search, KeyRound, X } from 'lucide-react';
import { AddGuardianData, GuardianRow, OrgGuardiansApi, OrgStudentOption, UpdateGuardianData } from '@/services/org-guardians.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Field, Input, Select } from '@/components/ui/Input';

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

const guardianSchema = z.object({
  firstName: z.string().min(1, 'Required'),
  lastName: z.string().optional().or(z.literal('')),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  mobile: z.string().min(1, 'Required'),
  gender: z.enum(['male', 'female']),
  username: z.string().optional().or(z.literal('')),
});
type GuardianFormValues = z.infer<typeof guardianSchema>;

/**
 * Org tier — real Guardian management. Guardians live in MASTER
 * (`guardian_directory` + `guardian_children`), per-student, never a
 * standalone per-school list — so the flow is School → Student → that
 * student's guardians, not a flat guardians table (there isn't one).
 */
export function OrgGuardiansPage() {
  const qc = useQueryClient();
  const [schoolId, setSchoolId] = useState('');
  const [studentSearch, setStudentSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [student, setStudent] = useState<OrgStudentOption | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(studentSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [studentSearch]);

  const { data: schools = [] } = useQuery({ queryKey: ['org-guardians-schools'], queryFn: () => OrgGuardiansApi.schools() });

  useEffect(() => {
    setStudent(null);
    setStudentSearch('');
  }, [schoolId]);

  const { data: suggestions = [], isFetching: searching } = useQuery({
    queryKey: ['org-guardians-students', schoolId, debounced],
    queryFn: () => OrgGuardiansApi.students(schoolId, debounced || undefined),
    enabled: !!schoolId && !student,
  });

  const { data: guardians = [], isLoading } = useQuery({
    queryKey: ['org-guardians', schoolId, student?.id],
    queryFn: () => OrgGuardiansApi.list(schoolId, student!.id),
    enabled: !!schoolId && !!student,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['org-guardians', schoolId, student?.id] });

  const [modal, setModal] = useState<{ open: boolean; guardian?: GuardianRow }>({ open: false });
  const [confirm, setConfirm] = useState<{ open: boolean; guardian?: GuardianRow }>({ open: false });
  const [pwdModal, setPwdModal] = useState<{ open: boolean; guardian?: GuardianRow }>({ open: false });

  const add = useMutation({
    mutationFn: (data: Omit<AddGuardianData, 'schoolId' | 'studentId'>) =>
      OrgGuardiansApi.add({ ...data, schoolId, studentId: student!.id }),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const update = useMutation({
    mutationFn: (v: { guardianId: string; data: Omit<UpdateGuardianData, 'schoolId'> }) =>
      OrgGuardiansApi.update(v.guardianId, { ...v.data, schoolId }),
    onSuccess: () => {
      invalidate();
      setModal({ open: false });
    },
  });

  const resetPwd = useMutation({
    mutationFn: (v: { guardianId: string; password: string }) => OrgGuardiansApi.resetPassword(v.guardianId, schoolId, v.password),
    onSuccess: () => setPwdModal({ open: false }),
  });

  const removeLink = useMutation({
    mutationFn: (linkId: string) => OrgGuardiansApi.removeLink(linkId, schoolId),
    onSuccess: () => {
      invalidate();
      setConfirm({ open: false });
    },
  });

  return (
    <>
      <PageHeader title="Guardians" description="Real parent/guardian records across your organization's schools. Pick a school, then a student, to manage their guardians." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-64" value={schoolId} onChange={(e) => setSchoolId(e.target.value)}>
          <option value="">— Select a school —</option>
          {schools.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>

        {schoolId && !student && (
          <div className="relative w-80">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              placeholder="Search student by name or admission #…"
              value={studentSearch}
              onChange={(e) => setStudentSearch(e.target.value)}
              className="w-full rounded-md border border-slate-200 py-1.5 pl-9 pr-3 text-sm"
            />
            {studentSearch && (
              <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                {searching ? (
                  <div className="p-3 text-center text-sm text-slate-400">Searching…</div>
                ) : suggestions.length === 0 ? (
                  <div className="p-3 text-center text-sm text-slate-400">No students found.</div>
                ) : (
                  suggestions.map((s) => (
                    <button
                      key={s.id}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-50"
                      onClick={() => setStudent(s)}
                    >
                      <span className="font-medium text-slate-900">{s.name ?? '(no linked user)'}</span>
                      <span className="text-slate-400">{s.admissionNo}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {student && (
          <div className="flex items-center gap-2 rounded-md bg-slate-100 px-3 py-1.5 text-sm">
            <span className="font-medium text-slate-900">{student.name ?? '(no linked user)'}</span>
            <span className="text-slate-500">{student.admissionNo}</span>
            <button className="text-slate-400 hover:text-slate-600" onClick={() => setStudent(null)} title="Change student">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {student && (
          <button className="btn-primary ml-auto" onClick={() => setModal({ open: true })}>
            <Plus className="mr-1.5 h-4 w-4" /> Add guardian
          </button>
        )}
      </div>

      {!schoolId ? (
        <div className="card p-8 text-center text-sm text-slate-400">Select a school to begin.</div>
      ) : !student ? (
        <div className="card p-8 text-center text-sm text-slate-400">Search for a student above to view their guardians.</div>
      ) : isLoading ? (
        <div className="card p-8 text-center text-sm text-slate-400">Loading…</div>
      ) : guardians.length === 0 ? (
        <div className="card p-8 text-center text-sm text-slate-400">No guardians linked to this student yet.</div>
      ) : (
        <div className="space-y-2">
          {guardians.map((g) => (
            <div key={g.linkId} className="card flex items-center justify-between gap-3 p-4">
              <div>
                <div className="font-medium text-slate-900">
                  {[g.firstName, g.lastName].filter(Boolean).join(' ') || '(unnamed)'}
                  {g.status !== 1 && (
                    <Badge tone="amber" className="ml-2">
                      Inactive
                    </Badge>
                  )}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-3 text-xs text-slate-500">
                  <span>Mobile: {g.mobile ?? '—'}</span>
                  <span>Email: {g.email ?? '—'}</span>
                  <span>Login: {g.username}</span>
                  {g.gender && <span className="capitalize">{g.gender}</span>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600" onClick={() => setModal({ open: true, guardian: g })} title="Edit">
                  <Pencil className="h-4 w-4" />
                </button>
                <button className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-brand-600" onClick={() => setPwdModal({ open: true, guardian: g })} title="Reset password">
                  <KeyRound className="h-4 w-4" />
                </button>
                <button className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setConfirm({ open: true, guardian: g })} title="Remove link">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <GuardianFormModal
        open={modal.open}
        guardian={modal.guardian}
        saving={add.isPending || update.isPending}
        errorMsg={errMsg(add.error) ?? errMsg(update.error)}
        onClose={() => setModal({ open: false })}
        onSubmit={(v) => {
          const payload = {
            firstName: v.firstName,
            lastName: v.lastName || undefined,
            email: v.email || undefined,
            mobile: v.mobile,
            gender: v.gender,
            username: v.username || undefined,
          };
          if (modal.guardian) update.mutate({ guardianId: modal.guardian.guardianId, data: payload });
          else add.mutate(payload);
        }}
      />

      <ResetPasswordModal
        open={pwdModal.open}
        guardianName={pwdModal.guardian ? [pwdModal.guardian.firstName, pwdModal.guardian.lastName].filter(Boolean).join(' ') : ''}
        saving={resetPwd.isPending}
        errorMsg={errMsg(resetPwd.error)}
        onClose={() => setPwdModal({ open: false })}
        onSubmit={(password) => pwdModal.guardian && resetPwd.mutate({ guardianId: pwdModal.guardian.guardianId, password })}
      />

      <ConfirmDialog
        open={confirm.open}
        onClose={() => setConfirm({ open: false })}
        onConfirm={() => confirm.guardian && removeLink.mutate(confirm.guardian.linkId)}
        loading={removeLink.isPending}
        title="Remove guardian link?"
        message={`Remove ${[confirm.guardian?.firstName, confirm.guardian?.lastName].filter(Boolean).join(' ')} from ${student?.name ?? 'this student'}. The guardian's own record (and any other children they're linked to) is not affected.`}
        confirmText="Remove"
      />
    </>
  );
}

function GuardianFormModal({
  open,
  guardian,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  guardian?: GuardianRow;
  onClose: () => void;
  onSubmit: (v: GuardianFormValues) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<GuardianFormValues>({
    resolver: zodResolver(guardianSchema),
    values: {
      firstName: guardian?.firstName ?? '',
      lastName: guardian?.lastName ?? '',
      email: guardian?.email ?? '',
      mobile: guardian?.mobile ?? '',
      gender: (guardian?.gender as 'male' | 'female') ?? 'male',
      username: guardian?.username ?? '',
    },
  });

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={guardian ? `Edit ${guardian.firstName ?? 'guardian'}` : 'Add guardian'}
      description={guardian ? undefined : 'Supplying an email that matches an existing real guardian reuses that record instead of creating a duplicate.'}
      size="lg"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : guardian ? 'Save changes' : 'Add guardian'}
          </button>
        </>
      }
    >
      <form className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="First name" required error={errors.firstName?.message}>
          <Input {...register('firstName')} />
        </Field>
        <Field label="Last name">
          <Input {...register('lastName')} />
        </Field>
        <Field label="Mobile" required error={errors.mobile?.message}>
          <Input {...register('mobile')} placeholder="9876543210" />
        </Field>
        <Field label="Gender" required>
          <Select {...register('gender')}>
            <option value="male">Male</option>
            <option value="female">Female</option>
          </Select>
        </Field>
        <Field label="Email" error={errors.email?.message}>
          <Input type="email" {...register('email')} />
        </Field>
        <Field label="Login username" error={errors.username?.message}>
          <Input {...register('username')} placeholder="Defaults to email, or mobile digits" />
        </Field>

        {errorMsg && <div className="sm:col-span-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
      </form>
    </Modal>
  );
}

function ResetPasswordModal({
  open,
  guardianName,
  onClose,
  onSubmit,
  saving,
  errorMsg,
}: {
  open: boolean;
  guardianName: string;
  onClose: () => void;
  onSubmit: (password: string) => void;
  saving: boolean;
  errorMsg?: string;
}) {
  const [password, setPassword] = useState('');
  useEffect(() => {
    if (open) setPassword('');
  }, [open]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reset password — ${guardianName}`}
      description="Sets a new mobile-app login password for this guardian immediately."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" disabled={saving || password.length < 4} onClick={() => onSubmit(password)}>
            {saving ? 'Resetting…' : 'Reset password'}
          </button>
        </>
      }
    >
      <Field label="New password" required>
        <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 4 characters" />
      </Field>
      {errorMsg && <div className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{errorMsg}</div>}
    </Modal>
  );
}
